import { getField, updateField } from "vuex-map-fields";
import { VuexModule, Module, Action } from "vuex-module-decorators";
import OfficeProfileService from "@/services/office-profile.service";
import ShiftService from "@/services/shift.service";
import { OfficeDefaultRateFormsDto, OfficeDto, OfficeFormDto, OfficeHourFormDto, OfficePayrollFormDto, OfficeProfileDto, OfficeUserDto, OfficeUserFormDto, OfficeStatusFormDto, OfficeDataDto } from "@/types/office.type";
import { PositionDto } from "@/types/position.type";
import { PracticeFormDto } from "@/types/practice.type";
import { UserFormDto } from "@/types/user.type";
import { AxiosError } from "axios";
import { Roles } from "@/types/enums.type";
import { StatusNames } from "@/common/constants";

@Module({ namespaced: true })
class OfficeProfile extends VuexModule {
  initialHourForm: OfficeHourFormDto = {
    officeId: 0,
    mondayOpenTime: "08:00",
    mondayCloseTime: "17:00",
    mondayBreakMinutes: 0,
    tuesdayOpenTime: "08:00",
    tuesdayCloseTime: "17:00",
    tuesdayBreakMinutes: 0,
    wednesdayOpenTime: "08:00",
    wednesdayCloseTime: "17:00",
    wednesdayBreakMinutes: 0,
    thursdayOpenTime: "08:00",
    thursdayCloseTime: "17:00",
    thursdayBreakMinutes: 0,
    fridayOpenTime: "08:00",
    fridayCloseTime: "17:00",
    fridayBreakMinutes: 0,
    saturdayOpenTime: "",
    saturdayCloseTime: "",
    saturdayBreakMinutes: 0,
    sundayOpenTime: "",
    sundayCloseTime: "",
    sundayBreakMinutes: 0
  };

  practiceName = "";
  selectedOffice: OfficeDto | null = null;
  officeProfiles: OfficeProfileDto[] = [];
  officeUsers: OfficeUserDto[] = [];
  positions: PositionDto[] = [];

  infoForm = new OfficeFormDto();
  rateForm = new OfficeDefaultRateFormsDto();
  payrollForm = new OfficePayrollFormDto();
  hourForm: OfficeHourFormDto = this.initialHourForm;

  officeData: OfficeDataDto | null = null;

  isEdit = false;
  pageLoading = false;
  
  errorMessage: string | null = null;

  @Action({ rawError: true })
  resetForm(): void {
    this.context.commit("updateField", {
      path: "infoForm",
      value: new OfficeFormDto()
    });
    this.context.commit("updateField", {
      path: "rateForm",
      value: new OfficeDefaultRateFormsDto()
    });
    this.context.commit("updateField", {
      path: "payrollForm",
      value: new OfficePayrollFormDto()
    });
    this.context.commit("updateField", {
      path: "hourForm",
      value: this.initialHourForm
    });
    this.context.commit("updateField", {
      path: "officeUsers",
      value: []
    });
  }

  @Action({ rawError: true })
  async getOfficeProfiles(practiceId: number): Promise<void> {
    try {
      const officeProfiles = await OfficeProfileService.getPracticeDetailsAsync(practiceId);
      
      if (!officeProfiles.length) {
        return;
      }

      this.context.commit("updateField", {
        path: "officeProfiles",
        value: officeProfiles
      });
      this.context.commit("updateField", {
        path: "practiceName",
        value: officeProfiles[0].practiceName
      });
    } catch (e: unknown) {
      const errorMessage = (e as AxiosError).response?.data.message;
      console.error(errorMessage);
    }
  }

  @Action({ rawError: true })
  async getOfficeUserData(): Promise<void> {
    const data = await OfficeProfileService.getOfficeDataAsync();

    this.context.commit("updateField", {
      path: "officeData",
      value: data
    });
  }

  @Action({ rawError: true })
  async getOfficeUsers({ practiceId, currentUserId }: { practiceId: number, currentUserId: number }): Promise<void> {
    const users = await OfficeProfileService.getUsersAsync(practiceId);

    if (this.isEdit && this.selectedOffice) {
      users.forEach(user => {
        if (!this.officeData || !this.selectedOffice) {
          return;
        }
        
        const disabledStatus = StatusNames.Disabled.getId(this.officeData.statuses);

        user.officeUserStatus = this.selectedOffice.users.find(officeUser => officeUser.userId === user.userId)?.officeUserStatus ?? disabledStatus;
      });
    }

    if (!this.isEdit) {
      users.find(user => {
        if (!this.officeData) {
          return;
        }

        if (user.roleName === Roles.PracticeUser || user.userId === currentUserId) {
          user.officeUserStatus = StatusNames.Enabled.getId(this.officeData.statuses);
        }
        else {
          user.officeUserStatus = StatusNames.Disabled.getId(this.officeData.statuses);
        }
      });
    }

    this.context.commit("updateField", {
      path: "officeUsers",
      value: users
    });
  }

  @Action({ rawError: true })
  async saveOffice(form: OfficeFormDto): Promise<number | undefined> {
    try {
      return await OfficeProfileService.saveOfficeAsync(form);
    } catch (e: unknown) {
      const errorMessage = (e as AxiosError).response?.data.message;
      console.error(errorMessage);
    }
  }

  @Action({ rawError: true })
  async saveOfficeRates(form: OfficeDefaultRateFormsDto): Promise<void> {
    try {
      await OfficeProfileService.saveOfficeRatesAsync(form);
    } catch (e: unknown) {
      const errorMessage = (e as AxiosError).response?.data.message;
      console.error(errorMessage);
    }
  }

  @Action({ rawError: true })
  async saveOfficeHours(form: OfficeHourFormDto): Promise<void> {
    try {
      await OfficeProfileService.saveOfficeHoursAsync(form);
    } catch (e: unknown) {
      const errorMessage = (e as AxiosError).response?.data.message;
      console.error(errorMessage);
    }
  }

  @Action({ rawError: true })
  async saveOfficeUser(form: OfficeUserFormDto): Promise<void> {
    try {
      await OfficeProfileService.saveOfficeUserAsync(form);
    } catch (e: unknown) {
      const errorMessage = (e as AxiosError).response?.data.message;
      console.error(errorMessage);
    }
  }

  @Action({ rawError: true })
  async savePayroll(form: OfficePayrollFormDto): Promise<void> {
    try {
      await OfficeProfileService.saveOfficePayrollAsync(form);
    } catch (e: unknown) {
      const errorMessage = (e as AxiosError).response?.data.message;
      console.error(errorMessage);
    }
  }

  @Action({ rawError: true })
  async savePractice(form: PracticeFormDto): Promise<void> {
    try {
      this.context.commit("updateField", {
        path: "practiceName",
        value: form.practiceName
      });

      await OfficeProfileService.savePracticeAsync(form);
    } catch (e: unknown) {
      const errorMessage = (e as AxiosError).response?.data.message;
      console.error(errorMessage);
    }
  }

  @Action({ rawError: true })
  async getPositions(): Promise<void> {
    try {
      const positions = await ShiftService.getPositionsAsync();

      this.context.commit("updateField", {
        path: "positions",
        value: positions
      });
    } catch (e: unknown) {
      const errorMessage = (e as AxiosError).response?.data.message;
      console.error(errorMessage);
    }
  }

  @Action({ rawError: true })
  async createUser(form: UserFormDto): Promise<number | undefined> {
    try {
      this.context.commit("updateField", {
        path: "errorMessage",
        value: null
      });

      return await OfficeProfileService.createUserAsync(form);
    } catch (e: unknown) {
      const errorMessage = (e as AxiosError).response?.data.message;
      this.context.commit("updateField", {
        path: "errorMessage",
        value: errorMessage
      });
    }
  }

  @Action({ rawError: true })
  async updateOfficeStatus(form: OfficeStatusFormDto): Promise<void> {
    await OfficeProfileService.updateOfficeStatus(form);
  }
}

OfficeProfile.getters = { getField };
OfficeProfile.mutations = { updateField };

export default OfficeProfile;